import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import ApiClient from "../apis/apiClient";
import { API_GetPropertySearches } from "../apis/constant";
import CsvDowloader from "../components/CsvDowloader";
import PropertySearchTable from "../components/property-search";
import SectionTitle from "../components/section-title";
import Widget from "../components/widget";

const columns = [
  { Header: "No", accessor: "no" },
  { Header: "First Name", accessor: "firstName" },
  { Header: "Last Name", accessor: "lastName" },
  { Header: "Email", accessor: "email" },
  { Header: "Mobile", accessor: "mobile" },
  { Header: "Property Services", accessor: "propertyServices" },
  { Header: "Property Type", accessor: "propertyType" },
  { Header: "Residency Status", accessor: "residencyStatus" },
  { Header: "Currently Own Property", accessor: "currentlyOwnProperty" },
  { Header: "Estimated Budget", accessor: "estimatedBudget" },
  { Header: "Loan Type", accessor: "loanType" },
  { Header: "HFE Approval", accessor: "hfeApproval" },
  { Header: "Recently Sold Private Property", accessor: "soldPrivateProperty" },
  { Header: "IPA Approval", accessor: "ipaApproval" },
  { Header: "Unit Type", accessor: "unitType" },
  { Header: "Preferred Location/MRT", accessor: "preferredLocation" },
  { Header: "Must Have Amenities", accessor: "mustHaveAmenities" },
  { Header: "Purchase Timeline", accessor: "purchaseTimeline" },
  { Header: "Additional Notes", accessor: "message" },
  { Header: "Submitted At", accessor: "createdAt" },
];

const formatValue = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  return value === undefined || value === null || value === "" ? "-" : value;
};

const formatPropertySearch = (item, no) => ({
  no,
  firstName: formatValue(item.firstName),
  lastName: formatValue(item.lastName),
  email: formatValue(item.email),
  mobile: formatValue(`${item.countryCode || ""}${item.phoneNumber || ""}`),
  propertyServices: formatValue(item.propertyServices),
  propertyType: formatValue(item.propertyType),
  residencyStatus: formatValue(item.residencyStatus),
  currentlyOwnProperty: formatValue(item.currentlyOwnProperty),
  estimatedBudget: formatValue(item.estimatedBudget),
  loanType: formatValue(item.loanType),
  hfeApproval: formatValue(item.hfeApproval),
  soldPrivateProperty: formatValue(item.soldPrivateProperty),
  ipaApproval: formatValue(item.ipaApproval),
  unitType: formatValue(item.unitType),
  preferredLocation: formatValue(item.preferredLocation),
  mustHaveAmenities: formatValue(item.mustHaveAmenities),
  purchaseTimeline: formatValue(item.purchaseTimeline),
  message: formatValue(item.message),
  createdAt: item.createdAt
    ? moment(item.createdAt).format("DD-MMM-YYYY hh:mm A")
    : "-",
});

const buildUrl = ({ limit, page, search, exportAll = false }) => {
  const params = new URLSearchParams();

  if (limit) params.set("limit", limit);
  if (page) params.set("page", page);
  if (search) params.set("searchBy", search);
  if (exportAll) params.set("export", "true");

  return `${API_GetPropertySearches}?${params.toString()}`;
};

const PropertySearch = () => {
  const [propertySearches, setPropertySearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    pages: 0,
    total: 0,
  });
  const adminToken = localStorage.getItem("wed_admin_token");

  const getPropertySearches = useCallback(
    async (limit = 10, page = 1) => {
      setLoading(true);

      try {
        const response = await ApiClient.get(
          buildUrl({ limit, page, search: search.trim() }),
          "",
          `Bearer ${adminToken}`,
        );

        if (response?.data?.code !== 200) {
          throw new Error(response?.data?.message || "Unable to load property searches.");
        }

        const result = response.data.data;
        const docs = result.docs || [];
        setPropertySearches(
          docs.map((item, index) =>
            formatPropertySearch(
              item,
              result.total - (result.page - 1) * result.limit - index,
            ),
          ),
        );
        setPagination({
          limit: result.limit,
          page: result.page,
          pages: result.pages,
          total: result.total,
        });
      } catch (error) {
        setPropertySearches([]);
        toast.error(error.message || "Unable to load property searches.");
      } finally {
        setLoading(false);
      }
    },
    [adminToken, search],
  );

  useEffect(() => {
    const timeout = setTimeout(() => getPropertySearches(10, 1), 300);
    return () => clearTimeout(timeout);
  }, [getPropertySearches]);

  const getExportData = useCallback(async () => {
    const response = await ApiClient.get(
      buildUrl({ search: search.trim(), exportAll: true }),
      "",
      `Bearer ${adminToken}`,
    );

    if (response?.data?.code !== 200) {
      throw new Error(response?.data?.message || "Unable to export property searches.");
    }

    const data = response.data.data || [];
    return data.map((item, index) => formatPropertySearch(item, data.length - index));
  }, [adminToken, search]);

  const tableData = useMemo(() => propertySearches, [propertySearches]);

  return (
    <>
      <SectionTitle title="" subtitle="Property Search" />
      <Widget>
        <div className="w-full flex items-center gap-2 mb-4">
          <input
            className="searchCss"
            type="text"
            placeholder="Search Property Requests"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <CsvDowloader
            columns={columns}
            data={tableData}
            dataLoader={getExportData}
            filename="property_searches"
            format="csv"
            onError={(error) =>
              toast.error(error.message || "Unable to export property searches.")
            }
          />
        </div>
        <PropertySearchTable
          columns={columns}
          data={tableData}
          loading={loading}
          pagination={pagination}
          onPageChange={(page) => getPropertySearches(pagination.limit, page)}
          onLimitChange={(limit) => getPropertySearches(limit, 1)}
        />
      </Widget>
    </>
  );
};

export default PropertySearch;
